import React, { useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonDatetime,
  IonButton,
  IonTextarea,
  useIonToast,
  IonSpinner,
  IonText,
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import getApi from '../api';
import EnvironmentBadge from '../components/EnvironmentBadge';
import { useLocalStorage } from '../components/useLocalStorage';

// Helper function to get weekdays between two dates (excluding weekends)
function getWeekdaysBetween(startDate: Date, endDate: Date): Date[] {
  const dates: Date[] = [];
  
  // Parse dates as local dates to avoid timezone issues
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Create dates in local timezone
  const current = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endLocal = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  while (current <= endLocal) {
    const dayOfWeek = current.getDay();
    // 0 = Sunday, 6 = Saturday
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      dates.push(new Date(current));
    }
    current.setDate(current.getDate() + 1);
  }

  return dates;
}

const Vacation: React.FC = () => {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [description, setDescription] = useState('Semester');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [present] = useIonToast();
  const history = useHistory();
  const api = getApi();
  const [me] = useLocalStorage('me', '');

  const handleSubmit = async () => {
    if (!startDate || !endDate) {
      present({
        message: 'Välj både start- och slutdatum',
        duration: 2000,
        color: 'warning',
        position: 'bottom',
      });
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      present({
        message: 'Startdatum kan inte vara efter slutdatum',
        duration: 2000,
        color: 'warning',
        position: 'bottom',
      });
      return;
    }

    const weekdays = getWeekdaysBetween(start, end);

    if (weekdays.length === 0) {
      present({
        message: 'Inga vardagar i det valda intervallet',
        duration: 2000,
        color: 'warning',
        position: 'bottom',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Register 8 hours for each weekday
      const promises = weekdays.map((date) => {
        // Format date as YYYY-MM-DD in local timezone
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        
        const timeData = {
          "date": dateString,
          "check_in": "08:00",
          "check_out": "16:00",
          "desc": description,
          "hours": "8",
          "order_id": "0",
          "traktamente": "",
          "free": "True",
          "user": window.localStorage.getItem("me") ? JSON.parse(window.localStorage.getItem("me")!).id : null,
          "edited": "False"
        };

        return api.post('/user/timeregister/', timeData);
      });

      await Promise.all(promises);

      present({
        message: `Semester registrerad för ${weekdays.length} vardag${weekdays.length > 1 ? 'ar' : ''}`,
        duration: 3000,
        color: 'success',
        position: 'bottom',
      });

      // Navigate back to time registration page
      setTimeout(() => {
        history.replace('/Tid');
      }, 1000);
    } catch (error) {
      console.error('Failed to register vacation', error);
      present({
        message: 'Det gick inte att registrera semestern',
        duration: 3000,
        color: 'danger',
        position: 'bottom',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const weekdayCount = startDate && endDate 
    ? getWeekdaysBetween(new Date(startDate), new Date(endDate)).length 
    : 0;

  return (
    <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
      <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonBackButton defaultHref="/Tid" />
          </IonButtons>
          <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Semester</IonTitle>
          <div slot="end" style={{ paddingRight: '8px' }}>
            <EnvironmentBadge />
          </div>
        </IonToolbar>
      </IonHeader>
      <IonContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Registrera semester</IonCardTitle>
          </IonCardHeader>
          <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Startdatum</IonLabel>
              <IonDatetime
                value={startDate}
                onIonChange={(e) => setStartDate(e.detail.value as string)}
                presentation="date"
                placeholder="Välj startdatum"
                firstDayOfWeek={1}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </IonItem>

            <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Slutdatum</IonLabel>
              <IonDatetime
                value={endDate}
                onIonChange={(e) => setEndDate(e.detail.value as string)}
                presentation="date"
                placeholder="Välj slutdatum"
                firstDayOfWeek={1}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </IonItem>

            <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Beskrivning</IonLabel>
              <IonTextarea
                value={description}
                onIonChange={(e) => setDescription(e.detail.value!)}
                placeholder="Beskrivning av semestern"
                rows={3}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </IonItem>

            {weekdayCount > 0 && (
              <IonItem lines="none" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <p>
                    Registrerar {weekdayCount} vardag{weekdayCount > 1 ? 'ar' : ''} à 8 timmar
                    ({weekdayCount * 8} timmar totalt)
                  </p>
                </IonText>
              </IonItem>
            )}

            <IonButton
              expand="block"
              onClick={handleSubmit}
              disabled={isSubmitting || !startDate || !endDate}
              style={{ marginTop: '20px' }}
              placeholder={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            >
              {isSubmitting ? (
                <>
                  <IonSpinner name="crescent" style={{ marginRight: '8px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                  Registrerar...
                </>
              ) : (
                'Registrera semester'
              )}
            </IonButton>
          </IonCardContent>
        </IonCard>
      </IonContent>
    </IonPage>
  );
};

export default Vacation;
